import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";

export type FocusLevel = 'minimal' | 'focused' | 'full';

interface FocusModeContextType {
  isFocusModeActive: boolean;
  focusLevel: FocusLevel;
  toggleFocusMode: () => void;
  enableFocusMode: () => void;
  disableFocusMode: () => void;
  setFocusLevel: (level: FocusLevel) => void;
  // Progressive focus features
  hideNavigation: boolean;
  reduceColors: boolean;
  simplifyActions: boolean;
  enableBreathingRoom: boolean;
}

const FocusModeContext = createContext<FocusModeContextType | undefined>(undefined);

export const useFocusMode = () => {
  const context = useContext(FocusModeContext);
  if (!context) {
    throw new Error("useFocusMode must be used within a FocusModeProvider");
  }
  return context;
};

interface FocusModeProviderProps {
  children: ReactNode;
}

export const FocusModeProvider: React.FC<FocusModeProviderProps> = ({ children }) => {
  const [isFocusModeActive, setIsFocusModeActive] = useState(() => {
    // Restore focus mode state from localStorage
    const saved = localStorage.getItem("flowcus-focus-mode");
    return saved ? JSON.parse(saved) : false;
  });

  const [focusLevel, setFocusLevel] = useState<FocusLevel>(() => {
    // Restore focus level from localStorage
    const saved = localStorage.getItem("flowcus-focus-level");
    return (saved as FocusLevel) || 'full';
  });

  // Calculate progressive focus features based on level
  const focusFeatures = {
    hideNavigation: focusLevel === 'minimal',
    reduceColors: focusLevel !== 'full',
    simplifyActions: focusLevel === 'minimal',
    enableBreathingRoom: focusLevel === 'minimal' || focusLevel === 'focused',
  };

  // Save focus mode state to localStorage
  useEffect(() => {
    localStorage.setItem("flowcus-focus-mode", JSON.stringify(isFocusModeActive));
  }, [isFocusModeActive]);

  // Save focus level to localStorage
  useEffect(() => {
    localStorage.setItem("flowcus-focus-level", focusLevel);
  }, [focusLevel]);

  // Keyboard shortcut handler (Ctrl/Cmd + F)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Ctrl+F or Cmd+F
      if (
        (event.ctrlKey || event.metaKey) &&
        event.key === "f" &&
        !event.shiftKey &&
        !event.altKey
      ) {
        // Only trigger if not in an input field
        const activeElement = document.activeElement;
        const isInputField =
          activeElement &&
          (activeElement.tagName === "INPUT" ||
            activeElement.tagName === "TEXTAREA" ||
            (activeElement as HTMLElement).contentEditable === "true");

        // If we're in a search context (like browser search), don't override
        // But if we're not, use our focus mode toggle
        if (!isInputField) {
          event.preventDefault();
          setIsFocusModeActive((prev: boolean) => !prev);
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Apply focus mode class to body for global styling
  useEffect(() => {
    if (isFocusModeActive) {
      document.body.classList.add("focus-mode-active");
    } else {
      document.body.classList.remove("focus-mode-active");
    }

    // Apply focus level classes
    document.body.classList.remove("focus-minimal", "focus-focused", "focus-full");
    document.body.classList.add(`focus-${focusLevel}`);

    // Apply feature classes for styling
    if (focusFeatures.hideNavigation) {
      document.body.classList.add("hide-navigation");
    } else {
      document.body.classList.remove("hide-navigation");
    }

    if (focusFeatures.reduceColors) {
      document.body.classList.add("reduce-colors");
    } else {
      document.body.classList.remove("reduce-colors");
    }

    if (focusFeatures.enableBreathingRoom) {
      document.body.classList.add("breathing-room");
    } else {
      document.body.classList.remove("breathing-room");
    }

    return () => {
      document.body.classList.remove("focus-mode-active", "focus-minimal", "focus-focused", "focus-full");
      document.body.classList.remove("hide-navigation", "reduce-colors", "breathing-room");
    };
  }, [isFocusModeActive, focusLevel, focusFeatures.hideNavigation, focusFeatures.reduceColors, focusFeatures.enableBreathingRoom]);

  const toggleFocusMode = useCallback(() => {
    setIsFocusModeActive((prev: boolean) => !prev);
  }, []);

  const enableFocusMode = useCallback(() => {
    setIsFocusModeActive(true);
  }, []);

  const disableFocusMode = useCallback(() => {
    setIsFocusModeActive(false);
  }, []);

  const value: FocusModeContextType = {
    isFocusModeActive,
    focusLevel,
    toggleFocusMode,
    enableFocusMode,
    disableFocusMode,
    setFocusLevel,
    ...focusFeatures,
  };

  return <FocusModeContext.Provider value={value}>{children}</FocusModeContext.Provider>;
};
